import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProjectById, deleteProject } from '../../../../Redux/Projects/projectSlice';
import DailyLogs from './Tabs/DailyLogs';
import Labor from './Tabs/Labor/Labor';
import Materials from './Tabs/Material/Materials';
import Expenses from './Tabs/Expense/Expenses';
import Analytics from './Tabs/Analytics/Analytics';
import {
  FaCalendarAlt,
  FaUserTie,
  FaBoxOpen,
  FaMoneyBillWave,
  FaChartLine,
  FaPen,
  FaEllipsisV,
  FaFileAlt
} from 'react-icons/fa';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import '../../../../Stylesheets/Dashboard/ProjectDetails.css';
import { toast } from 'react-toastify';
import EditProjectModal from './Modal/EditProjectModal';
import DeleteProjectModal from './Modal/DeleteProjectModal';
import ProjectReportModal from './Modal/ProjectReportModal';

const tabs = [
  { label: 'Daily Logs', icon: <FaCalendarAlt /> },
  { label: 'Labor', icon: <FaUserTie /> },
  { label: 'Materials', icon: <FaBoxOpen /> },
  { label: 'Expenses', icon: <FaMoneyBillWave /> },
  // { label: 'Analytics', icon: <FaChartLine /> },
];

const ProjectDetails = () => {
  const { projectId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('Daily Logs');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [showProjectReport, setShowProjectReport] = useState(false);

  // project-level report filters
  const [projectReportFrom, setProjectReportFrom] = useState('');
  const [projectReportTo, setProjectReportTo] = useState('');
  const [projectReportDetail, setProjectReportDetail] = useState('summary'); // or 'detailed'


  const { selectedProject: project, status, error } = useSelector((state) => state.projects);


  // ✅ read tab from URL (and validate)
  const tabFromUrl = useMemo(() => {
    const qs = new URLSearchParams(location.search);
    return qs.get('tab');
  }, [location.search]);


  useEffect(() => {
    if (!project) return;

    const start = project.startDate ? project.startDate.slice(0, 10) : '';
    const end = project.endDate ? project.endDate.slice(0, 10) : '';

    setProjectReportFrom(start);
    setProjectReportTo(end);
  }, [project]);


  useEffect(() => {
    if (!tabFromUrl) return;
    const valid = tabs.some(t => t.label === tabFromUrl);
    if (valid) setActiveTab(tabFromUrl);
  }, [tabFromUrl]);

  useEffect(() => {
    dispatch(fetchProjectById(projectId));
  }, [dispatch, projectId]);

  useEffect(() => {
    if (status === 'failed' && error) {
      toast.error(`Failed to load project: ${error}`, { position: 'top-right' });
    }
  }, [status, error]);

  const calculateProgress = () => {
    if (!project?.startDate || !project?.endDate) return 0;
    const now = new Date();
    const start = new Date(project.startDate);
    const end = new Date(project.endDate);
    if (now < start) return 0;
    if (now > end) return 100;
    const total = end - start;
    const done = now - start;
    return Math.floor((done / total) * 100);
  };

  const handleAction = (action) => {
    setDropdownOpen(false);
    if (action === 'Edit Project') setEditOpen(true);
    if (action === 'Delete Project') setDeleteOpen(true);

    toast.info(`${action} clicked`, { position: 'top-right' });
  };

  const renderTab = () => {
    switch (activeTab) {
      case 'Daily Logs': return <DailyLogs currentProjectId={projectId} />;
      case 'Labor': return <Labor currentProjectId={projectId} />;
      case 'Materials': return <Materials currentProjectId={projectId} />;
      case 'Expenses': return <Expenses currentProjectId={projectId} />;
      case 'Analytics': return <Analytics />;
      default: return null;
    }
  };

  const handleOpenProjectReportModal = () => {
    if (!project) return;
    setShowProjectReport(true);
  };


  const handleDeleteProject = async () => {
    try {
      await dispatch(deleteProject(project._id)).unwrap();
      toast.success('Project deleted successfully!');
      navigate('/dashboard/projects');
    } catch (err) {
      toast.error(err || 'Failed to delete project');
    }
  };


  const handleProjectReportPreview = () => {
    if (!projectId || !projectReportFrom || !projectReportTo) return;

    const qs = new URLSearchParams({
      projectId,
      from: projectReportFrom,
      to: projectReportTo,
      detail: projectReportDetail || 'summary',
      type: 'project',   // you'll handle this in ProjectReportPreview + backend
    }).toString();

    setShowProjectReport(false);
    navigate(`/dashboard/project-report-preview?${qs}`);
  };



  if (status === 'loading' || !project) {
    return (
      <div className="project-details-wrapper">
        <Skeleton height={30} width={220} />
        <div className="project-info-cards">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} height={90} borderRadius={10} />
          ))}
        </div>
        <div className="project-tabs">
          {tabs.map((tab, i) => (
            <Skeleton key={i} height={30} width={100} style={{ marginRight: 12 }} />
          ))}
        </div>
      </div>
    );
  }

  const progress = calculateProgress();

  return (
    <div className="project-details-wrapper">
      <div className="breadcrumb-wrapper">
        <p className="breadcrumb-text">
          <Link to="/dashboard/projects" className="breadcrumb-link">Projects</Link> / {project.name}
        </p>
        <div className="project-title-row">
          <h1 className="project-title">
            {project.name}
          </h1>

          <div className="project-header-actions">
            <button
              className="project-report-btn"
              onClick={handleOpenProjectReportModal}
            >
              <FaFileAlt className="project-report-icon" />
              Project Report
            </button>
            <div className="action-dropdown-wrapper">
              <FaEllipsisV className="action-menu-icon" onClick={() => setDropdownOpen(!dropdownOpen)} />
              {dropdownOpen && (
                <div className="action-dropdown">
                  <p onClick={() => handleAction('Edit Project')}>Edit Project</p>
                  <p onClick={() => handleAction('Delete Project')}>Delete Project</p>
                  {/* <p onClick={() => handleAction('Archive Project')}>Archive Project</p> */}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="project-info-cards">
        <div className="info-card dual-card">
          <div className="dual-section">
            <label>Start Date</label>
            <p>{new Date(project.startDate).toLocaleDateString('en-IN')}</p>
          </div>
          <div className="vertical-divider"></div>
          <div className="dual-section">
            <label>End Date</label>
            <p>{new Date(project.endDate).toLocaleDateString('en-IN')}</p>
          </div>
        </div>

        <div className="info-card">
          <label>Progress</label>
          <div className="progress-container">
            <span className="progress-value">{progress}%</span>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%` }}></div>
            </div>
          </div>
        </div>

        <div className="info-card editable-field">
          <label>Budget</label>
          <p>₹{Number(project.budget).toLocaleString('en-IN')} </p>
        </div>

        <div className="info-card editable-field">
          <label>Location</label>
          <p>{project.location}</p>
        </div>
      </div>

      <div className="project-tabs">
        {tabs.map(({ label, icon }) => (
          <button
            key={label}
            className={activeTab === label ? 'tab-btn active' : 'tab-btn'}
            onClick={() => {
              setActiveTab(label);
              // ✅ keep tab in URL so refresh/back stays on same tab
              const next = new URLSearchParams(location.search);
              next.set('tab', label);
              navigate({ search: next.toString() }, { replace: true });
            }}
          >
            <span className="tab-icon">{icon}</span>
            {label}
          </button>
        ))}
      </div>

      <div className="tab-content">
        {renderTab()}
      </div>



      {editOpen && <EditProjectModal project={project} onClose={() => setEditOpen(false)} />}
      {deleteOpen && (
        <DeleteProjectModal
          onCancel={() => setDeleteOpen(false)}
          onConfirm={handleDeleteProject}
        />
      )}


      <ProjectReportModal
        show={showProjectReport}
        onClose={() => setShowProjectReport(false)}
        startDate={projectReportFrom}
        endDate={projectReportTo}
        setStartDate={setProjectReportFrom}
        setEndDate={setProjectReportTo}
        detail={projectReportDetail}
        setDetail={setProjectReportDetail}
        onOpenPreview={handleProjectReportPreview}
      />


    </div>
  );
};

export default ProjectDetails;
